import { jwtDecode } from 'jwt-decode';

export function isAdmin(accessToken: string) {
  return userHasRole(accessToken, "IaC.Admin");
}

export function userHasRole(accessToken: string, roleName: string) {
  const decodedToken: any = jwtDecode(accessToken);
  return decodedToken.roles && decodedToken.roles.includes(roleName);
}

export function getUserEmail(accessToken: string): string | null {
  try {
    const decodedToken: any = jwtDecode(accessToken);
    return decodedToken?.preferred_username || decodedToken?.email || null;
  } catch (e) {
    return null;
  }
}

export function buildAdoPrUrl(repositoryUrl: string | undefined, prId: number | undefined): string | null {
  if (!repositoryUrl || !prId) {
    return null;
  }
  const match = repositoryUrl.match(/^(https:\/\/dev\.azure\.com\/[^/]+\/[^/]+)\/_apis\/git\/repositories\/([^/]+)/);
  return match ? `${match[1]}/_git/${match[2]}/pullrequest/${prId}` : null;
}

export function renderWithEval(template: string, context: any) {
  return template.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_, code) => {
    try {
      // Whitelist: only allow characters present in valid template expressions:
      // identifiers, string literals (single/double quoted), numbers, safe operators,
      // brackets, property access (.), comma, space/tab.
      // Implicitly blocks: backticks, semicolons, newlines, backslashes (unicode escapes)
      if (!/^[a-zA-Z0-9_$'".:,+\-*/?!<>=&|()[\]{} \t]+$/.test(code)) {
        console.warn(`[SECURITY] Blocked expression with disallowed characters: ${code}`);
        return '';
      }

      // Block assignment operator (single = that is not part of ==, ===, !=, !==, <=, >=, =>)
      if (/(?<![=!<>])=(?![=>])/.test(code)) {
        console.warn(`[SECURITY] Blocked assignment operator in expression: ${code}`);
        return '';
      }

      // Block JS keywords and browser globals not needed in template expressions
      if (/\b(new|function|return|var|let|const|class|this|window|document|globalThis|self|fetch|XMLHttpRequest|__proto__|prototype|constructor)\b/.test(code)) {
        console.warn(`[SECURITY] Blocked dangerous keyword in expression: ${code}`);
        return '';
      }

      // Shadow browser globals (e.g. window.location) that are not part of the form context
      const safeContext = { location: undefined, ...context };
      // eslint-disable-next-line no-new-func
      const func = new Function(...Object.keys(safeContext), `return ${code};`);
      const result = func(...Object.values(safeContext));
      // Return '' for missing values so backend guards (e.g. location === "") work correctly
      // and prevent unnecessary downstream API calls (e.g. to vSphere)
      if (result === undefined || result === null) return '';
      return result;
    } catch (e) {
      return '';
    }
  });
}

export interface DynamicOption {
  value: string;
  label: string;
}

export function resolveDynamicOptions(
  expression: string,
  formData: any
): DynamicOption[] {
  try {
    const dangerousPatterns = [
      /require\s*\(/i,
      /import\s+/i,
      /eval\s*\(/i,
      /Function\s*\(/i,
      /process\./i,
      /global\./i,
      /__dirname/i,
      /__filename/i,
      /child_process/i,
      /fs\./i,
      /exec\s*\(/i,
      /spawn\s*\(/i,
    ];

    for (const pattern of dangerousPatterns) {
      if (pattern.test(expression)) {
        console.warn(`[SECURITY] Blocked dangerous pattern in dynamic options: ${expression}`);
        return [];
      }
    }

    let result;
    
    // First try: evaluate with provided formData
    try {
      // eslint-disable-next-line no-new-func
      const func = new Function(...Object.keys(formData), `return ${expression};`);
      result = func(...Object.values(formData));
    } catch (err) {
      // If ReferenceError (undefined variable), retry with extended context
      if (err instanceof ReferenceError) {
        const varPattern = /\b[a-zA-Z_][a-zA-Z0-9_]*\b/g;
        const usedVars = expression.match(varPattern) || [];
        const extendedFormData = { ...formData };
        
        usedVars.forEach(varName => {
          if (!['true', 'false', 'null', 'undefined', 'Array', 'Object', 'String', 'Number', 'Boolean'].includes(varName)) {
            if (extendedFormData[varName] === undefined) {
              extendedFormData[varName] = false;
            }
          }
        });
        
        // eslint-disable-next-line no-new-func
        const func = new Function(...Object.keys(extendedFormData), `return ${expression};`);
        result = func(...Object.values(extendedFormData));
      } else {
        throw err; // Re-throw non-ReferenceError errors
      }
    }
    
    if (!result) {
      return [];
    }

    if (Array.isArray(result)) {
      return result.map(item => {
        if (typeof item === 'string') {
          return { value: item, label: item };
        } else if (typeof item === 'object' && item.value) {
          return {
            value: String(item.value),
            label: item.label || String(item.value)
          };
        }
        return { value: String(item), label: String(item) };
      });
    }
    
    if (typeof result === 'object' && result !== null) {
      return Object.entries(result).map(([key, value]) => ({
        value: key,
        label: String(value)
      }));
    }

    return [];
  } catch (err) {
    console.error("[DynamicOptions] Failed to resolve expression:", err);
    return [];
  }
}

export function stripHtml(html: string): string {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export function transformLocation(location: string, locationNameToRegion: Record<string, string> = {}): string {
  return locationNameToRegion[location] || location;
}